import { Edge, Node, MarkerType, XYPosition } from "reactflow";
import invariant from "ts-invariant";
import {
  DeleteFilled,
  ClusterOutlined,
  QuestionOutlined,
  MessageFilled,
  SmileOutlined,
  TagOutlined,
  UserDeleteOutlined,
  TeamOutlined,
  SearchOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  ButtonOption,
  isWhenUserResponse,
  TriggerStepCreate,
  WhenPredicate,
  WhenUserResponse,
  isPlaceholderStep,
  TriggerForEditor,
  AnyTriggerTransition,
  isInternalStepId,
  isEmbedMsgStepId,
  StepConfigSendSurvey,
  WhenButtonUserResponse,
  when2String,
  isQuestionMsgStepId,
  isIfConditionStepId,
} from "@dots-projects/types";
import { PlaceholderData, TypedNode } from "./reducer/stateUtils";
import { HashtagIcon } from "./icons/steps/HashtagIcon";
import { CardIcon } from "./icons/steps/CardIcon";
import { RobotIcon } from "./icons/steps/RobotIcon";
import { BriefcaseIcon } from "./icons/steps/BriefcaseIcon";
import { GuildRole } from "./StepFormEditRoles";
import produce from "immer";
import { ZapierIcon } from "./icons/steps/ZapierIcon";

const commonNodeAttrs = {
  position: { x: 0, y: 0 },
};

const commonEdgeAttrs: Partial<Edge> = {
  type: "floating",
  style: {
    stroke: "#000",
    strokeWidth: 2,
    opacity: 1,
  },
  // Since React Flow 11.5, we need to set the source and target position explicitly. Otherwise, the edge will not be rendered. The actual position is determined in FloatingEdge.tsx
  sourceHandle: "bottom",
  targetHandle: "top",
  markerEnd: {
    type: MarkerType.Arrow,
    color: "#000",
    strokeWidth: 2,
  },
};

const sendMessageIcon = (id: string) => {
  if (isQuestionMsgStepId(id)) {
    return <QuestionOutlined className="w-4" />;
  } else if (isEmbedMsgStepId(id)) {
    return <p className="font-semibold text-base m-0">{"{}"}</p>;
  } else {
    return <CardIcon className="w-4" />;
  }
};

function getStepData(step: TriggerStepCreate) {
  //TODO use { [key in TaskType]: string } instead
  const typeConfig: { [key: string]: { icon?: any; type: string } } = {
    send_message: {
      icon: sendMessageIcon(step.id),
      type: step.name || "Send a message",
    },
    send_survey: {
      icon: <ClusterOutlined className="w-4" />,
      type: step.name || "Send a survey (single selection)",
    },
    send_multiple_select: {
      icon: <ClusterOutlined className="w-4" />,
      type: step.name || "Send a survey (multiple selections)",
    },
    create_channel: {
      icon: <HashtagIcon />,
      type: step.name || "Create a channel",
    },
    test_captcha: {
      icon: <RobotIcon />,
      type: step.name || "Send a captcha",
    },
    delete_channel: {
      icon: <DeleteFilled />,
      type: "Delete a channel",
    },
    edit_roles: {
      icon: <BriefcaseIcon />,
      type: step.name || "Edit user roles",
    },
    edit_dots_tags: {
      icon: <TagOutlined />,
      type: "Edit Dots tags of users",
    },
    open_modal: {
      icon: <CardIcon className="w-4" />,
      type: step.name || "Show a modal",
    },
    create_thread: {
      icon: <MessageFilled />,
      type: step.name || "Create a thread",
    },
    send_webhook: {
      icon: <ZapierIcon />,
      type: step.name || "Push to Zapier",
    },
    kick_member: {
      icon: <UserDeleteOutlined className="text-2xl" />,
      type: step.name || "Kick a member",
    },
    change_nickname: {
      icon: <TeamOutlined />,
      type: step.name || "Change nickname",
    },
    find_user: {
      icon: <SearchOutlined />,
      type: step.name || "Find user",
    },
    save_user_field: {
      icon: <EditOutlined />,
      type: step.name || "Save user field",
    },
  };
  let label;
  switch (step.type) {
    case "send_message":
    case "send_survey":
    case "test_captcha":
      label = step.configs.message;
      break;
    case "create_thread":
      label = step.configs.title;
      break;
    case "edit_dots_tags":
      label = `Add: ${step.configs.addTags?.length} - Remove: ${step.configs.removeTags?.length}`;
      break;
    case "edit_roles":
      let changedRoles: GuildRole[] = [];
      if (step.configs.addRoles) {
        for (const role of step.configs.addRoles) {
          // TODO: Push the role's name after implementing a way to add roles by role ID instead of role name
          // changedRoles.push(role)
        }
      }
      if (step.configs.removeRoles) {
        for (const role of step.configs.removeRoles) {
          // TODO: Push the role's name after implementing a way to add roles by role ID instead of role name
          // changedRoles.push(role.name)
        }
      }

      label = changedRoles.join(" ");
  }
  return {
    displaySettings: typeConfig[step.type] ?? {
      // capitalize first leeter of step unsupported type and replace underscore for a space
      type: `${step.type[0].toUpperCase()}${step.type
        .replace("_", " ")
        .substring(1)}`,
      icon: <SmileOutlined style={{ fontSize: "1.3rem" }} />,
    },
    label,
  };
}

function whenPredicateToString(w: WhenPredicate) {
  switch (typeof w) {
    case "number":
    case "string":
      return "" + w;
    case "object":
      return JSON.stringify(w);
    default:
      return w;
  }
}

function pushUniqueNode(node: TypedNode, outputNodes: Node[]) {
  const existing = outputNodes.find((n) => n.id === node.id);
  if (existing) {
    console.log("Node already exists! existing:", existing, "new:", node);
  } else {
    outputNodes.push(determineDraggableConnectable(node));
  }
}

function pushUniqueEdge(
  edge: Omit<Edge, "id"> & { id?: string },
  outputEdges: Edge[]
) {
  const newEdge = {
    id: `${edge.source}->${edge.target}`,
    ...edge,
  };
  const existing = outputEdges.find((n) => n.id === newEdge.id);
  if (existing) {
    console.log("Edge already exists! existing:", existing, "new:", newEdge);
  } else {
    outputEdges.push(newEdge);
  }
}

export function addPlaceholderNode(
  id: string,
  data: PlaceholderData,
  position: XYPosition,
  outputNodes: Node[]
) {
  pushUniqueNode(
    {
      ...commonNodeAttrs,
      id,
      type: "placeholder",
      data,
      position,
    },
    outputNodes
  );
}

/**
 *
 * Create a placeholder node that displays as "+" in the UI. A user can select this node to create a new step.
 *
 * @param param0
 * @param outputNodes
 * @param outputEdges
 */
function createAddPlaceholderNode(
  {
    id,
    parentNodeId,
    data,
    edgeLabel,
    edgeData,
    nodeIdSuffix,
  }: {
    id?: string;
    parentNodeId?: string;
    data?: any; //TODO type it
    edgeData?: any; //TODO type it
    edgeLabel?: string;
    nodeIdSuffix?: string;
  },
  outputNodes: Node[],
  outputEdges: Edge[]
) {
  const nodeId = id ?? `${parentNodeId}-add-${nodeIdSuffix ?? edgeLabel}`;
  pushUniqueNode(
    {
      ...commonNodeAttrs,
      id: nodeId,
      type: "placeholder",
      data,
    },
    outputNodes
  );
  if (parentNodeId) {
    pushUniqueEdge(
      {
        ...commonEdgeAttrs,
        source: parentNodeId,
        target: nodeId,
        label: edgeLabel,
        data: { ...edgeData },
      },
      outputEdges
    );
  }
}

function getWhenEdgeLabel(
  when: WhenUserResponse,
  transition: AnyTriggerTransition,
  options?: any[]
) {
  if (transition.name) {
    return transition.name;
  }
  const userResponse = when.userResponse;
  switch (userResponse.type) {
    case "button": {
      if (options) {
        const option = options.find((o) => o.id === userResponse.buttonId);
        if (option) {
          return option?.label;
        }
      }
      return whenPredicateToString(
        userResponse.buttonId ?? "User clicks a button"
      );
    }
    case "message":
      return whenPredicateToString(
        userResponse.content ?? "User sends a message"
      );
    case "captcha-result":
      return whenPredicateToString(
        userResponse.succeeded ? "Captcha passes" : "Captcha fails"
      );
  }
  return userResponse.type;
}

/**
 * Reorder the nodes because the way as we create them is not the same as the order we want to display them.
 * because depending on if a node is a placeholder or not, we create it before or after causing
 * the order to be wrong, the way as it works:
 * 1 -All step nodes (including placeholders) with an incoming transition will be ordered first, except the option nodes from a send_survey step. Their order is the same as in their corresponding transitions.
 * 2 -Step nodes (including placeholders) that appear as options of a send_survey step. The order is the same as in step.configs.options.
 * 3- All other nodes including the root node and conditional nodes (diamonds) will be ordered last.
 * We didn't have any issue with conditional nodes but maybe they should have some influence on the order, we need to keep our eyes on edge cases
 * regarding it.
 * @param nodes
 * @param edges
 * @param trigger
 */
export function reorderNodes(
  nodes: TypedNode[],
  edges: Edge[],
  trigger: TriggerForEditor
) {
  const optionSendSurveyNodes: TypedNode[] = [];

  //Send survey options will be ordered with the same order of options in the survey
  trigger.steps
    .filter((s) => s.type === "send_survey")
    .map((s) => {
      invariant(s.type === "send_survey");

      return s.configs.options?.map((o) => {
        //Currently, only buttons are supported, but I added support for other types just in case
        if (s.configs.type === "buttons") {
          const buttonOption = o as ButtonOption;
          return {
            id: buttonOption.id,
            label: buttonOption.label,
            stepId: s.id,
          };
        } else {
          return { id: o, label: o, stepId: s.id };
        }
      });
    })
    .flat()
    .forEach((o) => {
      //Find the edge that has the source as stepId and the label as the option label
      // we need to remove userResponse because it's not part of the stepId
      const edge = edges.find(
        (e) =>
          e.source.replace("userResponse-", "") === o.stepId &&
          e.data?.buttonId === o.id
      );
      if (edge && nodes.find((n) => n.id === edge?.target)) {
        optionSendSurveyNodes.push(
          nodes.find((n) => n.id === edge?.target) as TypedNode
        );
      }
    });

  //Get the nodes id that represent a send survey option
  const optionNodeIds = optionSendSurveyNodes.map((n) => n.id);

  //Push the nodes that are options first
  const filterOrderNodes = [
    ...optionSendSurveyNodes,
    ...nodes.filter((n) => !optionNodeIds.includes(n.id)),
  ];

  // For nodes other than send survey, we need to reorder them too but the logic is different
  // because we need to keep the order of the transitions either if the targetStepId is a placeholder or not
  const transitionOrdersNodes: string[] = [];
  trigger.steps
    .filter((s) => s.type !== "send_survey" && s.transitions.length)
    .forEach((s) => {
      s.transitions.forEach((t) => {
        transitionOrdersNodes.push(t.targetStepId);
      });
    });

  const findNode = (id: string) => {
    return nodes.find((node) => node.id === id);
  };

  const transitionNodes = transitionOrdersNodes
    .filter((nodeId) => {
      return findNode(nodeId);
    })
    .map((nodeId) => findNode(nodeId)) as TypedNode[];

  return [
    ...transitionNodes,
    ...filterOrderNodes.filter((n) => !transitionOrdersNodes.includes(n.id)),
  ];
}

/**
 * Reorder the edges because the way as we create them is not the same as the order we want to display them.
 * and the order is important to render the arrows correctly. The only edges that are being created in the wrong order are send survey options edges
 * The way as it works:
 * 1 - All edges that are that represents a send survey option will be ordered first, the order is the same as in step.configs.options.
 * @param edges
 * @param trigger
 */
export function reorderEdges(edges: Edge[], trigger: TriggerForEditor) {
  const optionSendSurveyEdges: Edge[] = [];

  //Send survey options will be ordered with the same order of options in the survey
  trigger.steps
    .filter((s) => s.type === "send_survey")
    .map((s) => {
      //Currently, only button options are supported
      return (s.configs as any).options?.map((o: any) => {
        return { id: o.id, label: o.label, stepId: s.id };
      });
    })
    .flat()
    .forEach((o) => {
      //Find the edge that has the source as stepId and the label as the option label
      // we need to remove userResponse because it's not part of the stepId
      const edge = edges.find(
        (e) =>
          e.source.replace("userResponse-", "") === o.stepId &&
          e.data?.buttonId === o.id
      ) as Edge;
      if (edge) {
        optionSendSurveyEdges.push(edge);
      }
    });

  //Get the edges id that represent a send survey option
  const optionEdgeIds = optionSendSurveyEdges.map((edge) => edge.id);

  //Push the edges that are options first
  return [
    ...optionSendSurveyEdges,
    ...edges.filter((edge) => !optionEdgeIds.includes(edge.id)),
  ];
}

export const TRIGGER_ROOT_ID = "trigger-root";
/**
 * Create nodes and edges for the flow chart from the given trigger
 *
 * This function could use some refactoring as it's fairly long.
 *
 * @param trigger
 * @returns `{nodes, edges}`
 */
export function createNodesAndEdges(trigger: TriggerForEditor): {
  nodes: TypedNode[];
  edges: Edge[];
} {
  const nodes: TypedNode[] = [];
  const edges: Edge[] = [];

  const entryStep = trigger.steps.find((s) => s.isEntry) ?? trigger.steps[0];

  const idleStepIds = new Set();

  // entry point
  pushUniqueNode(
    {
      ...commonNodeAttrs,
      id: TRIGGER_ROOT_ID,
      type: "trigger",
      data: {
        label: trigger.isDraft ? trigger.liveTriggerConfigs.type : trigger.type,
      },
    },
    nodes
  );
  if (entryStep) {
    pushUniqueEdge(
      {
        ...commonEdgeAttrs,
        source: TRIGGER_ROOT_ID,
        target: entryStep.id,
      },
      edges
    );
  } else {
    if (trigger.isDraft) {
      createAddPlaceholderNode(
        { parentNodeId: TRIGGER_ROOT_ID, data: { isFirstPlaceholder: true } },
        nodes,
        edges
      );
    }
  }

  trigger.steps.forEach((step) => {
    if (isIfConditionStepId(step.id)) {
      invariant(step.if);
      pushUniqueNode(
        {
          ...commonNodeAttrs,
          id: step.id,
          type: "if-else",
          data: {
            if: step.if,
            trigger,
          },
        },
        nodes
      );
      pushUniqueEdge(
        {
          ...commonEdgeAttrs,
          source: step.id,
          target: step.if.elseStepId,
          id: `${step.id}=>${step.if.elseStepId}-else`,
          label: "No",
          data: { labelOffsetX: -10, labelOffsetY: -40 },
        },
        edges
      );
    } else if (isPlaceholderStep(step)) {
      // create the placeholder node ("+") for placeholder steps in drafts
      if (trigger.isDraft) {
        createAddPlaceholderNode(
          { id: step.id, data: { parentStepId: step.id } },
          nodes,
          edges
        );
      } else {
        pushUniqueNode(
          {
            ...commonNodeAttrs,
            id: step.id,
            type: "placeholder-published",
            data: getStepData(step),
          },
          nodes
        );
      }
    } else if (isInternalStepId(step.id)) {
      // Skip other internal steps such as $$step-track-all-messages
      return;
    } else {
      if (step.type !== "idle") {
        // add every step
        pushUniqueNode(
          {
            ...commonNodeAttrs,
            id: step.id,
            type: "step",
            data: getStepData(step),
          },
          nodes
        );
      } else {
        idleStepIds.add(step.id);
      }
    }
    if (step.transitions.length > 0) {
      if (step.type === "send_multiple_select") {
        pushUniqueEdge(
          {
            ...commonEdgeAttrs,
            source: step.id,
            target: step.transitions[0].targetStepId,
          },
          edges
        );
        // Don't create the diamond node
        return;
      }
      // add transition edges and condition nodes
      step.transitions.forEach((t, transitionIndex) => {
        if (isWhenUserResponse(t.when)) {
          const conditionNodeId = `userResponse-${step.id}`;
          let conditionNodeLabel: string = "";

          // step to condition node
          pushUniqueEdge(
            {
              ...commonEdgeAttrs,
              source: step.id,
              target: conditionNodeId,
            },
            edges
          );
          // condition node
          pushUniqueNode(
            {
              ...commonNodeAttrs,
              type: "condition",
              id: conditionNodeId,
              data: { label: conditionNodeLabel, stepId: step.id },
            },
            nodes
          );

          const whenUserResponse = t.when as WhenUserResponse;
          const option =
            step.type === "send_survey" &&
            whenUserResponse.userResponse.type == "button"
              ? //TODO: Currently we only support button type in send survey, for other types we need to add support
                (
                  (step.configs as StepConfigSendSurvey).options as
                    | ButtonOption[]
                ).find(
                  (o: any) =>
                    o.id ===
                    (whenUserResponse.userResponse as WhenButtonUserResponse)
                      .buttonId
                )
              : null;

          // condition node to target step
          const edgeLabel = getWhenEdgeLabel(
            t.when,
            t,
            (step.configs as any)?.options
          );
          const whenAsString = when2String(t.when);
          pushUniqueEdge(
            {
              ...commonEdgeAttrs,
              source: conditionNodeId,
              target: t.targetStepId,
              id: `${conditionNodeId}->${t.targetStepId}-${whenAsString}`,
              data: {
                targetStepId: t.targetStepId,
                buttonId: option?.id,
                whenAsString,
                transitionIndex,
              },
              label: edgeLabel,
            },
            edges
          );
        } else {
          // direct connection

          const directTransitionStep = trigger.steps.find(
            (s) => s.id === t.targetStepId
          )!;

          // Not creating an edge for internal steps different from idle
          if (
            directTransitionStep && // directTransitionStep might be undefined when we use a subset of the graph for re-layout purpose (e.g. when updating a step)
            (directTransitionStep.type === "idle" ||
              !isInternalStepId(directTransitionStep.id))
          ) {
            pushUniqueEdge(
              {
                ...commonEdgeAttrs,
                source: step.id,
                target: t.targetStepId,
                label: isIfConditionStepId(step.id) ? "Yes" : "",
                data: {
                  transitionIndex,
                },
              },
              edges
            );
          }
        }
      });
    }
  });
  // post-processing: redirect all edges that point to idle steps to their corresponding diamonds
  edges.forEach((e) => {
    if (idleStepIds.has(e.target)) {
      const idleNodeId = e.target;
      const conditionNodeId = `userResponse-${idleNodeId}`; // TODO need a better way to construct conditionNodeId
      const conditionNode = nodes.find((n) => n.id === conditionNodeId);
      if (conditionNode) {
        e.target = conditionNodeId;
      }
    }
  });

  const nodesOrdered: TypedNode[] = reorderNodes(nodes, edges, trigger);
  const edgesOrdered: Edge[] = reorderEdges(edges, trigger);

  const finalEdges = insertTargetHandleIndexMetaData(edgesOrdered);
  const finalNodes = nodesOrdered.map(determineDraggableConnectable);

  return { nodes: finalNodes, edges: finalEdges };
}

function determineDraggableConnectable(node: TypedNode) {
  const draggable =
    node.type === "placeholder"
      ? !Boolean((node.data as PlaceholderData).isFloating)
      : true;
  return {
    ...node,
    draggable,
    connectable: node.type !== "trigger" && node.type !== "placeholder",
    data: { ...node.data, draggable },
  };
}

/**
 * Add `handleIndexOnSameTarget` and `handleCountOnSameTarget` to the data field of every edge. These two properties are used to determine the target position of the edge on a step node. Basically we want to avoid edges pointing to the same step node occlude each other. We can use these two values to add some gaps between the edges.
 *
 * See FloatingEdge.getEdgeParams.
 *
 * @param edges
 * @returns a new array of edges
 */
function insertTargetHandleIndexMetaData(edges: Edge[]) {
  return produce(edges, (draftEdges) => {
    const targetCountMap = new Map<string, number>();
    draftEdges.forEach((e) => {
      const count = targetCountMap.get(e.target) ?? 0;
      targetCountMap.set(e.target, count + 1);
    });

    const targetIndexMap = new Map<string, number>();
    draftEdges.forEach((e) => {
      const index = targetIndexMap.get(e.target) ?? 0;
      if (!e.data) e.data = {};
      e.data.handleIndexOnSameTarget = index;
      e.data.handleCountOnSameTarget = targetCountMap.get(e.target);
      targetIndexMap.set(e.target, index + 1);
    });
  });
}
